import { defineStore } from 'pinia';
import { reactive, UnwrapNestedRefs } from 'vue';

import {
  EXECUTORS_LOOKUPS_FEED_VIEW_FILTER_SELECT_ORDERS_FROM,
  EXECUTORS_LOOKUPS_FEED_VIEW_FILTER_SELECT_NAME,
} from '@/contexts/executorsLookupContext/views/ExecutorsLookupsFeedView/constants';
import {
  EOperationalDocumentsFlowFilters,
  OPERATIONAL_DOCUMENTS_VIEW_FILTER_SELECT_STATUS,
  OPERATIONAL_DOCUMENTS_VIEW_FILTER_SELECT_TYPE,
} from '@/contexts/operationalDocumentsContext/views/OperationalDocumentsListView/domain/constants';
import { STORE_FILTER_SUB_SECTION_GLOBAL, EFilterSections } from '@/stores/filter/constants';
import {
  EExecutionRequestsAgreementsFromFilterKeys,
  EXECUTION_REQUEST_TABLE_FILTER_SELECT_AGREEMENTS_FROM,
  EXECUTION_REQUEST_TABLE_FILTER_SELECT_ASSIGNED_QUANTITY,
  EXECUTION_REQUEST_TABLE_FILTER_SELECT_LOOKUPS_STATE,
  EXECUTION_REQUEST_TABLE_FILTER_SELECT_QUANTITY,
  EXECUTION_REQUEST_TABLE_FILTER_SELECT_TYPE,
} from '@/contexts/freightExecutionContext/constants';
import { ORDERS_FILTER_SELECT_DEPARTMENTS } from '@/contexts/orderingContext/views/OrdersListView/components/OrdersListOnlyOrders/components/OrdersDepartmentsFilter/constants';
import { ORDERS_FILTER_SELECT_INCIDENTS } from '@/contexts/orderingContext/views/OrdersListView/components/OrdersListOnlyOrders/components/OrdersIncidentsFilters/constants';
import { EPartnershipResourcesSubSections } from '@partnershipsContext/public/domain';
import { hasProperty } from '@/utils';
import { TFilterPresetDTO } from '@/components/Filter/types';
import { EOrdersFromFilterKeys } from '@/contexts/executorsLookupContext/domain/constants';

import {
  TFilterSelectState,
  TFilterStore,
  TMainFilterState,
} from './types';

const createDefaultCommonFilterState = (): TMainFilterState => ({
  currentAppliedSet: null,
  currentAppliedPreset: null,
  currentEditablePreset: null,
});

const createDefaultFilterSelectState = () => ({ current: null });

const createDefaultFilterSelectWithIdState = () => ({
  current: null,
  id: null,
});

const createDefaultOrdersFromState = () => ({ current: [EOrdersFromFilterKeys.allPartners] });
const createDefaultAgreementsFromState = () => ({ current: [EExecutionRequestsAgreementsFromFilterKeys.allPartners] });

const isValidFilter = (
  state: UnwrapNestedRefs<Record<EFilterSections, unknown>>,
  section: string,
  subSection: string,
  name?: string,
) => {
  if (!hasProperty(state, section)) {
    console.warn(`There is no filter section with "${section}" name.`);
    return false;
  }

  if (!hasProperty(state[section], subSection)) {
    console.warn(`There is no filter sub-section with "${subSection}" name in "${section}" section.`);
    return false;
  }

  if (name && !hasProperty(state[section][subSection], name)) {
    console.warn(`There is no filter with name "${name}" in "${section}/${subSection}".`);
    return false;
  }

  return true;
};

// eslint-disable-next-line @stylistic/max-len
const getDefaultGlobalSubSectionFilter = () => ({ [STORE_FILTER_SUB_SECTION_GLOBAL]: { filter: createDefaultCommonFilterState() } });

export const useFilterStore = defineStore('TMS_FILTER', (): TFilterStore => {
  const filters = reactive({
    [EFilterSections.executorsLookupsFeedView]: {
      [STORE_FILTER_SUB_SECTION_GLOBAL]: {
        [EXECUTORS_LOOKUPS_FEED_VIEW_FILTER_SELECT_ORDERS_FROM]:
          createDefaultOrdersFromState(),
      },
      new: {
        filter: createDefaultCommonFilterState(),
        [EXECUTORS_LOOKUPS_FEED_VIEW_FILTER_SELECT_NAME]: createDefaultFilterSelectState(),
      },
      bidding: {
        filter: createDefaultCommonFilterState(),
        [EXECUTORS_LOOKUPS_FEED_VIEW_FILTER_SELECT_NAME]: createDefaultFilterSelectState(),
      },
      finished: {
        filter: createDefaultCommonFilterState(),
        [EXECUTORS_LOOKUPS_FEED_VIEW_FILTER_SELECT_NAME]: createDefaultFilterSelectState(),
      },
    },
    [EFilterSections.orders]: {
      [STORE_FILTER_SUB_SECTION_GLOBAL]: { [ORDERS_FILTER_SELECT_DEPARTMENTS]: createDefaultFilterSelectWithIdState() },
      finalized: {
        filter: createDefaultCommonFilterState(),
        [ORDERS_FILTER_SELECT_INCIDENTS]: createDefaultFilterSelectState(),
      },
      onBidding: {
        filter: createDefaultCommonFilterState(),
        [ORDERS_FILTER_SELECT_INCIDENTS]: createDefaultFilterSelectState(),
      },
      onExecution: {
        filter: createDefaultCommonFilterState(),
        [ORDERS_FILTER_SELECT_INCIDENTS]: createDefaultFilterSelectState(),
      },
      onTargetOffering: {
        filter: createDefaultCommonFilterState(),
        [ORDERS_FILTER_SELECT_INCIDENTS]: createDefaultFilterSelectState(),
      },
      pending: {
        filter: createDefaultCommonFilterState(),
        [ORDERS_FILTER_SELECT_INCIDENTS]: createDefaultFilterSelectState(),
      },
    },
    [EFilterSections.operationalDocumentsView]: {
      [EOperationalDocumentsFlowFilters.all]: {
        filter: createDefaultCommonFilterState(),
        [OPERATIONAL_DOCUMENTS_VIEW_FILTER_SELECT_STATUS]: createDefaultFilterSelectState(),
        [OPERATIONAL_DOCUMENTS_VIEW_FILTER_SELECT_TYPE]: createDefaultFilterSelectState(),
      },
      [EOperationalDocumentsFlowFilters.inbox]: {
        filter: createDefaultCommonFilterState(),
        [OPERATIONAL_DOCUMENTS_VIEW_FILTER_SELECT_STATUS]: createDefaultFilterSelectState(),
        [OPERATIONAL_DOCUMENTS_VIEW_FILTER_SELECT_TYPE]: createDefaultFilterSelectState(),
      },
      [EOperationalDocumentsFlowFilters.outbox]: {
        filter: createDefaultCommonFilterState(),
        [OPERATIONAL_DOCUMENTS_VIEW_FILTER_SELECT_STATUS]: createDefaultFilterSelectState(),
        [OPERATIONAL_DOCUMENTS_VIEW_FILTER_SELECT_TYPE]: createDefaultFilterSelectState(),
      },
      [EOperationalDocumentsFlowFilters.drafts]: {
        filter: createDefaultCommonFilterState(),
        [OPERATIONAL_DOCUMENTS_VIEW_FILTER_SELECT_STATUS]: createDefaultFilterSelectState(),
        [OPERATIONAL_DOCUMENTS_VIEW_FILTER_SELECT_TYPE]: createDefaultFilterSelectState(),
      },
    },
    [EFilterSections.matricesListView]: {},
    [EFilterSections.referencesListView]: {},
    [EFilterSections.executionRequestsView]: {
      [STORE_FILTER_SUB_SECTION_GLOBAL]: {
        filter: createDefaultCommonFilterState(),
        [EXECUTION_REQUEST_TABLE_FILTER_SELECT_TYPE]: createDefaultFilterSelectState(),
        [EXECUTION_REQUEST_TABLE_FILTER_SELECT_QUANTITY]: createDefaultFilterSelectState(),
        [EXECUTION_REQUEST_TABLE_FILTER_SELECT_ASSIGNED_QUANTITY]: createDefaultFilterSelectState(),
        [EXECUTION_REQUEST_TABLE_FILTER_SELECT_LOOKUPS_STATE]: createDefaultFilterSelectState(),
        [EXECUTION_REQUEST_TABLE_FILTER_SELECT_AGREEMENTS_FROM]: createDefaultAgreementsFromState(),
      },
    },
    [EFilterSections.partnershipResources]: {
      [EPartnershipResourcesSubSections.vehicles]: { filter: createDefaultCommonFilterState() },
      [EPartnershipResourcesSubSections.drivers]: { filter: createDefaultCommonFilterState() },
    },
    [EFilterSections.digitalQueueView]: getDefaultGlobalSubSectionFilter(),
    [EFilterSections.shipments]: getDefaultGlobalSubSectionFilter(),
    [EFilterSections.executionRequestPanelTabRuns]: getDefaultGlobalSubSectionFilter(),
    [EFilterSections.combinedExecutionRequestRuns]: getDefaultGlobalSubSectionFilter(),
    [EFilterSections.combinedOrderRuns]: getDefaultGlobalSubSectionFilter(),
    [EFilterSections.orderInfoDrawerTabRuns]: getDefaultGlobalSubSectionFilter(),
    [EFilterSections.employeesResponsible]: getDefaultGlobalSubSectionFilter(),
    [EFilterSections.selfBilling]: getDefaultGlobalSubSectionFilter(),
  });

  const getFilterState = ({
    section,
    subSection,
  }: {
    section: string,
    subSection: string,
  }): TMainFilterState | undefined => filters?.[section]?.[subSection]?.filter;

  const getFilterStateByName = ({
    section,
    subSection,
    name,
  }: {
    section: string,
    subSection: string,
    name: string,
  }): TFilterSelectState | undefined => {
    if (!isValidFilter(
      filters,
      section,
      subSection,
      name,
    )) return undefined;
    return filters[section][subSection][name];
  };

  const setMainFilterAppliedSet = ({
    section,
    subSection,
    preset,
  }: {
    section: string,
    subSection: string,
    preset: TFilterPresetDTO | null,
  }) => {
    if (!isValidFilter(
      filters,
      section,
      subSection,
      'filter',
    )) return;

    filters[section][subSection].filter.currentAppliedSet = preset ?? null;
  };

  const setMainFilterAppliedPreset = ({
    section,
    subSection,
    preset,
  }: {
    section: string,
    subSection: string,
    preset: TFilterPresetDTO | null,
  }) => {
    if (!isValidFilter(
      filters,
      section,
      subSection,
      'filter',
    )) return;

    filters[section][subSection].filter.currentAppliedPreset = preset ?? null;
  };

  const setMainFilterEditablePreset = ({
    section,
    subSection,
    preset,
  }: {
    section: string,
    subSection: string,
    preset: TFilterPresetDTO | null,
  }) => {
    if (!isValidFilter(
      filters,
      section,
      subSection,
      'filter',
    )) return;

    filters[section][subSection].filter.currentEditablePreset = preset ?? null;
  };

  const setSelectFilter = ({
    section,
    subSection,
    name,
    current,
  }: {
    section: string,
    subSection: string,
    name: string,
    current: string[] | string | boolean | null,
  }) => {
    if (!isValidFilter(
      filters,
      section,
      subSection,
      name,
    )) return;

    filters[section][subSection][name].current = current ?? null;
  };

  const setSelectFilterWithId = ({
    section,
    subSection,
    name,
    current,
    id,
  }: {
    section: string,
    subSection: string,
    name: string,
    current: string[] | string | boolean | null,
    id: string | null,
  }) => {
    if (!isValidFilter(
      filters,
      section,
      subSection,
      name,
    )) return;

    filters[section][subSection][name].current = current ?? null;
    filters[section][subSection][name].id = id ?? null;
  };

  const setSubsections = ({ section, subSections }: { section: string, subSections: string[] }) => {
    if (!section) return;
    subSections.forEach((subSection: string) => {
      if (!filters[section][subSection]?.filter) {
        filters[section][subSection] = { filter: createDefaultCommonFilterState() };
      }
    });
  };

  const $reset = () => {
    Object.keys(filters).forEach((section: string) => {
      switch (section) {
        case EFilterSections.executorsLookupsFeedView: {
          filters[section][STORE_FILTER_SUB_SECTION_GLOBAL] = {
            [EXECUTORS_LOOKUPS_FEED_VIEW_FILTER_SELECT_ORDERS_FROM]:
              createDefaultOrdersFromState(),
          };

          Object.keys(filters[section]).forEach((subSection: string) => {
            if (subSection !== STORE_FILTER_SUB_SECTION_GLOBAL) {
              filters[section][subSection] = {
                filter: createDefaultCommonFilterState(),
                [EXECUTORS_LOOKUPS_FEED_VIEW_FILTER_SELECT_NAME]: createDefaultFilterSelectState(),
              };
            }
          });
          break;
        }
        case EFilterSections.orders: {
          Object.keys(filters[section]).forEach((subSection: string) => {
            if (subSection === STORE_FILTER_SUB_SECTION_GLOBAL) {
              // eslint-disable-next-line @stylistic/max-len
              filters[section][subSection] = { [ORDERS_FILTER_SELECT_DEPARTMENTS]: createDefaultFilterSelectWithIdState() };
            } else {
              filters[section][subSection] = {
                filter: createDefaultCommonFilterState(),
                [ORDERS_FILTER_SELECT_INCIDENTS]: createDefaultFilterSelectState(),
              };
            }
          });
          break;
        }
        case EFilterSections.operationalDocumentsView: {
          Object.keys(filters[section]).forEach((subSection: string) => {
            filters[section][subSection] = {
              filter: createDefaultCommonFilterState(),
              [OPERATIONAL_DOCUMENTS_VIEW_FILTER_SELECT_STATUS]: createDefaultFilterSelectState(),
              [OPERATIONAL_DOCUMENTS_VIEW_FILTER_SELECT_TYPE]: createDefaultFilterSelectState(),
            };
          });
          break;
        }
        case EFilterSections.partnershipResources: {
          Object.keys(filters[section]).forEach((subSection: string) => {
            filters[section][subSection] = { filter: createDefaultCommonFilterState() };
          });
          break;
        }
        case EFilterSections.executionRequestsView: {
          filters[section][STORE_FILTER_SUB_SECTION_GLOBAL] = {
            filter: createDefaultCommonFilterState(),
            [EXECUTION_REQUEST_TABLE_FILTER_SELECT_TYPE]: createDefaultFilterSelectState(),
            [EXECUTION_REQUEST_TABLE_FILTER_SELECT_QUANTITY]: createDefaultFilterSelectState(),
            [EXECUTION_REQUEST_TABLE_FILTER_SELECT_ASSIGNED_QUANTITY]: createDefaultFilterSelectState(),
            [EXECUTION_REQUEST_TABLE_FILTER_SELECT_LOOKUPS_STATE]: createDefaultFilterSelectState(),
            [EXECUTION_REQUEST_TABLE_FILTER_SELECT_AGREEMENTS_FROM]: createDefaultAgreementsFromState(),
          };
          break;
        }
        case EFilterSections.matricesListView:
        case EFilterSections.referencesListView: {
          filters[section] = {};
          break;
        }
        default: {
          filters[section] = getDefaultGlobalSubSectionFilter();
          break;
        }
      }
    });
  };

  return {
    filters,

    getFilterState,
    getFilterStateByName,

    setMainFilterAppliedSet,
    setMainFilterAppliedPreset,
    setMainFilterEditablePreset,
    setSelectFilter,
    setSelectFilterWithId,
    setSubsections,
    $reset,
  };
}, { persist: true });
